import { cn } from '@/utilities/ui'
import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center whitespace-nowrap rounded',
    'text-sm ring-offset-background transition-colors cursor-pointer',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    '[:disabled,[disabled],[aria-disabled="true"]]:pointer-events-none',
    '[:disabled,[disabled],[aria-disabled="true"]]:opacity-50',
    '[:disabled,[disabled],[aria-disabled="true"]]:text-black',
    '[:disabled,[disabled],[aria-disabled="true"]]:cursor-not-allowed',
  ),
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        clear: '',
        default: 'h-10 px-4 py-2',
        icon: 'h-10 w-10',
        lg: 'h-11 rounded px-8',
        sm: 'h-9 rounded px-3',
        rounded: 'h-12 w-[196px] rounded-[40px]',
      },
      variant: {
        default: cn(
          'bg-primary text-primary-foreground',
          'hover:bg-primary/90',
          'active:bg-primary/80',
        ),
        destructive: cn(
          'bg-destructive text-destructive-foreground',
          'hover:bg-destructive/90',
          'active:bg-destructive/70',
        ),
        ghost: cn(
          'hover:bg-tenant-subtle-accent-background',
          'hover:text-tenant-primary-action-color',
          'active:bg-tenant-subtle-accent-background',
          'active:text-tenant-primary-action-pressed-color',
        ),
        link: cn(
          'text-primary items-start justify-start underline-offset-4',
          'hover:underline',
          'active:text-tenant-primary-action-pressed-color',
        ),
        outline: cn(
          'text-foreground border border-border bg-background',
          'hover:bg-card hover:text-accent-foreground',
          'active:bg-card active:text-tenant-primary-action-pressed-color',
        ),
        secondary: cn(
          'bg-secondary text-secondary-foreground',
          'hover:bg-secondary/80',
          'active:bg-secondary/60',
          'active:text-tenant-primary-action-pressed-color',
        ),
        light: cn(
          'bg-white text-tenant-primary-action-color',
          'hover:bg-white/80',
          'hover:text-tenant-primary-action-hover-color',
          'active:text-tenant-primary-action-pressed-color',
          'disabled:text-tenant-description-text disabled:bg-tenant-border disabled:opacity-100',
        ),
      },
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  ref?: React.Ref<HTMLButtonElement>
}

const Button: React.FC<ButtonProps> = ({
  asChild = false,
  className,
  size,
  variant,
  ref,
  ...props
}) => {
  const Comp = asChild ? Slot : 'button'
  return <Comp className={cn(buttonVariants({ className, size, variant }))} ref={ref} {...props} />
}

export { Button, buttonVariants }
